import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Type, RotateCcw } from "lucide-react";
import { Field, inputCls } from "../forms";
import { adminSetContent } from "../../lib/api";
import { CONTENT_MANIFEST } from "../../data/content";

/**
 * Onglet « Contenus » : modifie les textes et images de chaque page du site.
 * Un champ vide = la valeur par défaut du site est utilisée.
 */
export function ContentPanel({ token, overrides, onSaved }) {
  const [page, setPage] = useState(CONTENT_MANIFEST[0]?.page || "");
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => setEdits({}), [overrides]);

  const current = CONTENT_MANIFEST.find((p) => p.page === page);
  const fields = current?.fields || [];

  const valueOf = (key) => edits[key] ?? overrides[key] ?? "";
  const isCustom = (key) => (edits[key] ?? overrides[key] ?? "").length > 0;
  const dirtyCount = useMemo(
    () => Object.entries(edits).filter(([k, v]) => v !== (overrides[k] ?? "")).length,
    [edits, overrides]
  );

  const set = (key) => (e) => setEdits((s) => ({ ...s, [key]: e.target.value }));

  const resetField = (key) => setEdits((s) => ({ ...s, [key]: "" }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const merged = { ...overrides, ...edits };
      await adminSetContent(token, merged);
      toast.success("Contenus enregistrés, le site est à jour.");
      onSaved();
    } catch {
      toast.error("Impossible d'enregistrer les contenus.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3" data-testid="content-page-filters">
        {CONTENT_MANIFEST.map((p) => (
          <button key={p.page} onClick={() => setPage(p.page)} data-testid={`content-page-${p.page}`}
            className={`rounded-full border px-5 py-2 text-xs font-medium uppercase tracking-[0.15em] transition-colors duration-300 ${
              page === p.page ? "border-clay bg-clay text-cream" : "border-linen text-smoke hover:border-clay hover:text-clay"
            }`}>
            {p.label}
          </button>
        ))}
      </div>

      <form data-testid="content-form" onSubmit={onSubmit}
        className="mt-8 max-w-3xl rounded-[1.75rem] border border-linen bg-shell p-8 md:p-10">
        <h2 className="font-serif text-2xl font-light tracking-tight md:text-3xl">
          Textes et images : <em className="text-clay">{current?.label}</em>
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-smoke">
          Modifiez ce que vous voulez : chaque champ vide garde la valeur par défaut du site.
          La valeur actuelle par défaut apparaît en gris dans le champ.
        </p>

        <div className="mt-8 space-y-7">
          {fields.map((f) => (
            <div key={f.key}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <Field label={f.label + (isCustom(f.key) ? " · personnalisé" : "")} htmlFor={`content-${f.key}`}>
                    {f.type === "long" ? (
                      <textarea id={`content-${f.key}`} data-testid={`content-field-${f.key}`} rows={3}
                        value={valueOf(f.key)} onChange={set(f.key)} placeholder={f.def}
                        className={`${inputCls} resize-y`} />
                    ) : (
                      <input id={`content-${f.key}`} data-testid={`content-field-${f.key}`} type="text"
                        value={valueOf(f.key)} onChange={set(f.key)} placeholder={f.def}
                        className={inputCls} />
                    )}
                  </Field>
                  {f.type === "image" && (
                    <img src={valueOf(f.key) || f.def} alt="" data-testid={`content-preview-${f.key}`}
                      className="mt-3 h-16 w-16 rounded-xl border border-linen object-cover" />
                  )}
                </div>
                {isCustom(f.key) && (
                  <button type="button" onClick={() => resetField(f.key)} data-testid={`content-reset-${f.key}`}
                    title="Revenir à la valeur par défaut"
                    className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-linen px-3 py-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-ash transition-colors duration-300 hover:border-clay hover:text-clay">
                    <RotateCcw className="h-3 w-3" />
                    Défaut
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <button type="submit" data-testid="content-save-button" disabled={saving || dirtyCount === 0}
            className="group inline-flex items-center gap-2 rounded-full bg-clay px-8 py-4 text-xs font-medium uppercase tracking-[0.15em] text-cream transition-colors duration-300 hover:bg-charcoal disabled:opacity-60">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Type className="h-4 w-4" />}
            {saving ? "Enregistrement" : dirtyCount > 0 ? `Enregistrer (${dirtyCount} modification${dirtyCount > 1 ? "s" : ""})` : "Aucune modification"}
          </button>
        </div>
      </form>
    </div>
  );
}
